import { activateDrawer, lockScroll, unlockScroll, bindDrawerEscape } from '@theme/coco-utilities';

if (!customElements.get('product-info-tabs')) {
  class ProductInfoTabs extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('product-info-tabs-drawer');
      if (this.drawer) this.drawer.tabsRoot = this;
      this.addEventListener('click', this.onTriggerClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onTriggerClick);
      if (this.drawer) this.drawer.tabsRoot = null;
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-product-info-tab-open]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      this.drawer?.open(trigger, trigger.dataset.index ?? '0');
    }

    syncInlineTriggers(tabIndex) {
      if (tabIndex == null) return;

      const index = String(tabIndex);
      this.querySelectorAll('[data-product-info-tab-open]').forEach((button) => {
        const isActive = button.dataset.index === index;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('tabindex', isActive ? '0' : '-1');
        button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('product-info-tabs', ProductInfoTabs);
}

if (!customElements.get('product-info-tabs-drawer')) {
  class ProductInfoTabsDrawer extends HTMLElement {
    constructor() {
      super();
      this.tabsRoot = null;
      this.activeElement = null;
      this.releaseModal = null;
      this.unbindEscape = null;
      this.onClick = this.onClick.bind(this);
      this.onTabNavClick = this.onTabNavClick.bind(this);
    }

    connectedCallback() {
      this.tabsRoot = this.tabsRoot || this.closest('product-info-tabs');
      this.addEventListener('click', this.onClick);
      this.unbindEscape = bindDrawerEscape(this, () => this.close());
      this.querySelector('tab-component')?.addEventListener('click', this.onTabNavClick);
      this.moveToBody();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.unbindEscape?.();
      this.unbindEscape = null;
      this.querySelector('tab-component')?.removeEventListener('click', this.onTabNavClick);
      this.releaseModal?.();
      this.releaseModal = null;
    }

    moveToBody() {
      if (this.parentElement === document.body) return;
      document.body.appendChild(this);
    }

    get tabComponent() {
      return this.querySelector('tab-component');
    }

    get headingElement() {
      return this.querySelector('[data-product-info-tabs-drawer-heading]');
    }

    activateTab(tabIndex) {
      if (tabIndex == null) return;

      const index = String(tabIndex);
      if (typeof this.tabComponent?.showTab === 'function') {
        this.tabComponent.showTab(index);
      }

      this.tabsRoot?.syncInlineTriggers(index);
      this.syncHeading(index);
    }

    syncHeading(tabIndex) {
      const heading = this.headingElement;
      if (!this.tabsRoot || !heading || tabIndex == null) return;

      const activeButton = this.tabsRoot.querySelector(
        `[data-product-info-tab-open][data-index="${tabIndex}"]`
      );
      const label = activeButton?.querySelector('[data-tab-item-name]')?.textContent?.trim();
      if (label) heading.textContent = label;
    }

    onTabNavClick(event) {
      const tabItem = event.target.closest('[data-tab-item]');
      if (!tabItem) return;

      requestAnimationFrame(() => {
        this.activateTab(tabItem.dataset.index);
      });
    }

    onClick(event) {
      if (!this.hasAttribute('active')) return;

      const inner = this.querySelector('[data-drawer-inner]');
      const isInsidePanel = inner?.contains(event.target);

      if (event.target.closest('[data-product-info-tabs-close], [is="close-drawer"]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (!isInsidePanel) {
        event.preventDefault();
        this.close();
      }
    }

    open(triggeredBy, tabIndex = '0') {
      if (triggeredBy) {
        this.activeElement = triggeredBy;
        this.tabsRoot = triggeredBy.closest('product-info-tabs') || this.tabsRoot;
      }

      const isAlreadyOpen = this.hasAttribute('active');

      if (!isAlreadyOpen) {
        this.setAttribute('open', '');
        lockScroll();

        this.releaseModal?.();
        this.releaseModal = activateDrawer(this, {
          initialFocus: (drawer) =>
            drawer.querySelector(
              '[data-product-info-tabs-close]:not([data-fixed-overlay]), [is="close-drawer"]:not([data-fixed-overlay])'
            ),
        });
      }

      requestAnimationFrame(() => {
        if (!isAlreadyOpen) this.setAttribute('active', '');
        requestAnimationFrame(() => {
          this.activateTab(tabIndex);
        });
      });
    }

    close() {
      if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

      this.removeAttribute('active');

      const trigger =
        this.activeElement?.isConnected
          ? this.activeElement
          : this.tabsRoot?.querySelector('[data-product-info-tab-open][aria-expanded="true"]');
      trigger?.setAttribute?.('aria-expanded', 'false');

      this.releaseModal?.(trigger);
      this.releaseModal = null;
      this.activeElement = null;

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        this.removeAttribute('open');
        unlockScroll();
      };

      const inner = this.querySelector('[data-drawer-inner]');
      if (inner) {
        const onTransitionEnd = (event) => {
          if (event.target !== inner || event.propertyName !== 'transform') return;
          finish();
        };
        inner.addEventListener('transitionend', onTransitionEnd);
      }

      setTimeout(finish, 650);
    }
  }

  customElements.define('product-info-tabs-drawer', ProductInfoTabsDrawer);
}

if (!customElements.get('product-info-tabs-inline')) {
  class ProductInfoTabsInline extends HTMLElement {
    constructor() {
      super();
      this.onTabClick = this.onTabClick.bind(this);
    }

    connectedCallback() {
      this.tabComponent = this.querySelector('tab-component');
      this.tabsPanel = this.tabComponent?.querySelector('[data-tabs-panel]');

      if (this.dataset.openFirstTab === 'false') {
        this.initClosedState();
      }

      this.tabComponent?.addEventListener('click', this.onTabClick);
    }

    disconnectedCallback() {
      this.tabComponent?.removeEventListener('click', this.onTabClick);
    }

    initClosedState() {
      if (!this.tabComponent || !this.tabsPanel) return;

      this.tabComponent.querySelectorAll('[data-tab-content]').forEach((panel) => {
        panel.setAttribute('hidden', '');
      });

      this.tabComponent.querySelectorAll('[data-tab-item]').forEach((button) => {
        button.classList.remove('active');
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('tabindex', '-1');
      });

      this.tabsPanel.setAttribute('hidden', '');
    }

    onTabClick(event) {
      const tabItem = event.target.closest('[data-tab-item]');
      if (!tabItem) return;
      this.tabsPanel?.removeAttribute('hidden');
    }
  }

  customElements.define('product-info-tabs-inline', ProductInfoTabsInline);
}
